<script>
  import { onMount, onDestroy } from "svelte";
  import {
    Card,
    CardHeader,
    CardContent,
    Button,
    Icon,
    Badge,
    f7,
  } from "framework7-svelte";
  import { BASE_URL } from "../services/service.config";
  import { formatTimestamp, getEventBadgeClass } from "../js/audit-utils";
  import AuditDecisionCardModal from "./AuditDecisionCardModal.svelte";
  import AuditFanoutModal from "./AuditFanoutModal.svelte";
  import AuditOrderTimelineModal from "./AuditOrderTimelineModal.svelte";
  import AuditReconModal from "./AuditReconModal.svelte";

  export let environment = "LIVE"; // "LIVE" | "SIMULATION"
  export let users = [];

  let events = [];
  let isLoading = false;
  let hasMore = false;
  let nextCursor = null;
  let currentCursor = null;
  let cursorStack = []; // history of page request cursors for back navigation
  let lastEventId = null;

  // Filters
  let filterEventType = "ALL";
  let filterUcc = "";
  let filterStrategy = "ALL";
  let filterSide = "ALL";
  let filterSymbol = "";
  let timeFormat = "UTC"; // "UTC" | "IST"

  // Modals state
  let showDecisionModal = false;
  let selectedDecisionEvent = null;

  let showFanoutModal = false;
  let selectedSignalId = null;

  let showTimelineModal = false;
  let selectedOrderTag = null;

  let showReconModal = false;

  // Polling / Stream buffer
  let pollInterval = null;
  let pendingBuffer = [];
  let bufferFlushTimer = null;

  let isMounted = false;

  $: userMap = new Map(users.map(u => [(u.ucc || u.client_tag || '').toUpperCase(), u]));

  $: if (environment && isMounted) {
    resetAndFetch();
  }

  onMount(() => {
    isMounted = true;
    // Poll for live events every 3 seconds with 200ms throttle buffer
    pollInterval = setInterval(checkForNewEvents, 3000);
  });

  onDestroy(() => {
    if (pollInterval) clearInterval(pollInterval);
    if (bufferFlushTimer) clearTimeout(bufferFlushTimer);
  });

  function resetAndFetch() {
    events = [];
    nextCursor = null;
    currentCursor = null;
    cursorStack = [];
    lastEventId = null;
    fetchEvents('next', null);
  }

  async function fetchEvents(direction = 'next', cursor = null) {
    isLoading = true;
    try {
      let url = `${BASE_URL}/api/v1/audit/events?environment=${environment}&limit=40`;
      
      if (filterEventType && filterEventType !== 'ALL') {
        url += `&event_type=${encodeURIComponent(filterEventType)}`;
      }
      if (filterUcc.trim()) {
        url += `&ucc=${encodeURIComponent(filterUcc.trim())}`;
      }
      if (filterStrategy && filterStrategy !== 'ALL') {
        url += `&strategy_id=${encodeURIComponent(filterStrategy)}`;
      }
      if (filterSymbol.trim()) {
        url += `&symbol=${encodeURIComponent(filterSymbol.trim().toUpperCase())}`;
      }

      if (cursor && cursor.cursor_created_at && cursor.cursor_id) {
        url += `&cursor_created_at=${encodeURIComponent(cursor.cursor_created_at)}&cursor_id=${cursor.cursor_id}&direction=${direction}`;
      }

      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const data = await res.json();

      events = Array.isArray(data.events) ? data.events : [];
      hasMore = Boolean(data.has_more);
      nextCursor = data.next_cursor || null;

      if (events.length > 0) {
        lastEventId = events[0].event_id || events[0].id;
      }
    } catch (err) {
      console.error("Failed to load audit events:", err);
      f7.toast.create({
        text: `Failed to load audit events: ${err.message}`,
        closeTimeout: 3000,
      }).open();
    } finally {
      isLoading = false;
    }
  }

  async function checkForNewEvents() {
    if (!lastEventId || isLoading || cursorStack.length > 0) return;

    try {
      const res = await fetch(`${BASE_URL}/api/v1/audit/events/backfill?since_event_id=${encodeURIComponent(lastEventId)}&environment=${environment}&limit=20`);
      if (!res.ok) return;
      const data = await res.json();
      const newItems = data.events || (Array.isArray(data) ? data : []);
      
      if (newItems.length > 0) {
        // Advance latest pointer
        lastEventId = newItems[newItems.length - 1].event_id || newItems[newItems.length - 1].id;

        // Filter new items by active UI filters before buffering
        const matchingItems = newItems.filter(item => {
          if (filterEventType && filterEventType !== 'ALL' && item.event_type !== filterEventType) return false;
          if (filterUcc.trim() && (item.client_ucc || '').toUpperCase() !== filterUcc.trim().toUpperCase()) return false;
          if (filterStrategy && filterStrategy !== 'ALL' && item.strategy_id !== filterStrategy) return false;
          if (filterSymbol.trim() && (item.symbol || '').toUpperCase() !== filterSymbol.trim().toUpperCase()) return false;
          return true;
        });

        if (matchingItems.length > 0) {
          pendingBuffer.push(...matchingItems);
          if (!bufferFlushTimer) {
            bufferFlushTimer = setTimeout(flushBuffer, 200);
          }
        }
      }
    } catch (e) {
      // Non-blocking background sync error
    }
  }

  function flushBuffer() {
    bufferFlushTimer = null;
    if (pendingBuffer.length === 0) return;

    // Deduplicate by event_id or id
    const existingIds = new Set(events.map(e => e.event_id || e.id));
    const toPrepend = [];
    
    for (const item of pendingBuffer) {
      const key = item.event_id || item.id;
      if (!existingIds.has(key)) {
        existingIds.add(key);
        toPrepend.push(item);
      }
    }

    if (toPrepend.length > 0) {
      // Sort newest first
      toPrepend.sort((a, b) => new Date(b.created_at || b.timestamp) - new Date(a.created_at || a.timestamp));
      events = [...toPrepend, ...events].slice(0, 100);
      lastEventId = events[0].event_id || events[0].id;
    }
    pendingBuffer = [];
  }

  function handleNextPage() {
    if (!nextCursor || !hasMore) return;
    cursorStack.push(currentCursor);
    currentCursor = nextCursor;
    fetchEvents('next', currentCursor);
  }

  function handlePrevPage() {
    if (cursorStack.length === 0) return;
    currentCursor = cursorStack.pop();
    fetchEvents('next', currentCursor);
  }

  function openDecisionCard(event) {
    selectedDecisionEvent = event;
    showDecisionModal = true;
  }

  function openFanoutModal(signalId) {
    selectedSignalId = signalId;
    showFanoutModal = true;
  }

  function openOrderTimeline(orderTag) {
    selectedOrderTag = orderTag;
    showTimelineModal = true;
  }

  function handleExportCsv() {
    let exportUrl = `${BASE_URL}/api/v1/audit/export?environment=${environment}&format=csv`;
    if (filterUcc.trim()) exportUrl += `&ucc=${encodeURIComponent(filterUcc.trim())}`;
    if (filterEventType && filterEventType !== 'ALL') exportUrl += `&event_type=${encodeURIComponent(filterEventType)}`;
    if (filterStrategy && filterStrategy !== 'ALL') exportUrl += `&strategy_id=${encodeURIComponent(filterStrategy)}`;
    
    window.open(exportUrl, '_blank');
    f7.toast.create({
      text: "📥 Initiated streaming CSV export for audit ledger",
      closeTimeout: 3000,
    }).open();
  }

  function getBadgeClass(type) {
    return getEventBadgeClass(type);
  }
</script>

<div class="audit-tab-wrapper">
  <!-- Top Filter & Control Header -->
  <div class="audit-controls-card">
    <div class="filter-row">
      <!-- Event Type Filter -->
      <div class="filter-item">
        <label>Event Type</label>
        <select bind:value={filterEventType} on:change={resetAndFetch}>
          <option value="ALL">ALL Events</option>
          <option value="SIGNAL_TRIGGERED">⚡ SIGNAL_TRIGGERED</option>
          <option value="RMS_PASSED">🛡️ RMS_PASSED</option>
          <option value="RMS_REJECTED">🚫 RMS_REJECTED</option>
          <option value="ORDER_DISPATCHED">📤 ORDER_DISPATCHED</option>
          <option value="ORDER_ACKNOWLEDGED">📨 ORDER_ACKNOWLEDGED</option>
          <option value="PARTIAL_FILL">🌓 PARTIAL_FILL</option>
          <option value="ORDER_FILLED">✅ ORDER_FILLED</option>
          <option value="ORDER_CANCELLED">❌ ORDER_CANCELLED</option>
          <option value="ORDER_REJECTED">⛔ ORDER_REJECTED</option>
          <option value="TRAILING_SL_UPDATED">📈 TRAILING_SL_UPDATED</option>
          <option value="AUTO_MIS_SQUARE_OFF">⏰ AUTO_MIS_SQUARE_OFF</option>
          <option value="ADMIN_OVERRIDE">👤 ADMIN_OVERRIDE</option>
        </select>
      </div>

      <!-- UCC Tag Filter -->
      <div class="filter-item">
        <label>Client UCC</label>
        <input
          type="text"
          placeholder="Filter by UCC..."
          bind:value={filterUcc}
          on:keyup={(e) => e.key === 'Enter' && resetAndFetch()}
        />
      </div>

      <!-- Strategy ID Filter -->
      <div class="filter-item">
        <label>Strategy</label>
        <select bind:value={filterStrategy} on:change={resetAndFetch}>
          <option value="ALL">All Strategies</option>
          <option value="S1_INTRADAY_MOMENTUM">S1 Momentum</option>
          <option value="S2_TREND_BREAKOUT">S2 Trend</option>
          <option value="REV_MEAN_REVERSION">REV Mean Rev</option>
          <option value="CNC_LONG_TERM">CNC Long Term</option>
        </select>
      </div>

      <!-- Symbol Filter -->
      <div class="filter-item">
        <label>Symbol</label>
        <input
          type="text"
          placeholder="e.g. RELIANCE"
          bind:value={filterSymbol}
          on:keyup={(e) => e.key === 'Enter' && resetAndFetch()}
        />
      </div>

      <!-- Timezone Toggle -->
      <div class="filter-item">
        <label>Timezone</label>
        <div class="time-toggle">
          <button class="{timeFormat === 'UTC' ? 'active' : ''}" on:click={() => (timeFormat = 'UTC')}>UTC</button>
          <button class="{timeFormat === 'IST' ? 'active' : ''}" on:click={() => (timeFormat = 'IST')}>IST</button>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons-group">
        <Button small fill color="blue" onClick={resetAndFetch} disabled={isLoading}>
          <Icon f7="arrow_clockwise" size="14" /> &nbsp; {isLoading ? 'Loading...' : 'Query'}
        </Button>
        <Button small outline color="green" onClick={handleExportCsv}>
          <Icon f7="arrow_down_doc" size="14" /> &nbsp; Export CSV
        </Button>
        <Button small outline color="purple" onClick={() => (showReconModal = true)}>
          <Icon f7="checkmark_seal" size="14" /> &nbsp; EOD Recon
        </Button>
      </div>
    </div>
  </div>

  <!-- Audit Ledger Data Table -->
  <Card class="data-table audit-ledger-card margin-top">
    <CardHeader class="display-flex justify-content-space-between align-items-center">
      <div class="display-flex align-items-center gap-8">
        <strong>📜 Flight Recorder: Immutable Execution Ledger ({events.length} Loaded)</strong>
        <span class="env-pill pill-live">
          LIVE
        </span>
      </div>
      <div class="keyset-nav">
        <Button small outline color="blue" disabled={cursorStack.length === 0 || isLoading} onClick={handlePrevPage}>
          ◀ Prev
        </Button>
        <Button small outline color="blue" disabled={!hasMore || isLoading} onClick={handleNextPage}>
          Next ▶
        </Button>
      </div>
    </CardHeader>
    <CardContent padding={false}>
      <div class="table-scroll-wrap">
        <table class="audit-table">
          <thead>
            <tr>
              <th>Time ({timeFormat})</th>
              <th>Event Type</th>
              <th>UCC & User</th>
              <th>Symbol</th>
              <th>Leg</th>
              <th>Side</th>
              <th>Qty</th>
              <th>Price / Slip</th>
              <th>Forensic Explanation / Condition</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {#if events.length === 0}
              <tr>
                <td colspan="10" class="empty-row">
                  {#if isLoading}
                    <div class="spinner-inline"></div> Fetching audit trail records...
                  {:else}
                    No audit records matching query filters.
                  {/if}
                </td>
              </tr>
            {:else}
              {#each events as evt (evt.id || evt.event_id)}
                <tr class="audit-row" on:click={() => openDecisionCard(evt)}>
                  <td class="time-cell">
                    <code>{formatTimestamp(evt.created_at || evt.timestamp)}</code>
                  </td>
                  <td>
                    <span class="event-pill {getBadgeClass(evt.event_type)}">
                      {evt.event_type}
                    </span>
                  </td>
                  <td>
                    {#if evt.client_ucc}
                      <div class="user-cell">
                        <span class="ucc-name"><strong>{evt.client_ucc}</strong></span>
                        {#if userMap.get(evt.client_ucc.toUpperCase())}
                          <span class="user-fullname">{userMap.get(evt.client_ucc.toUpperCase()).fullName}</span>
                        {/if}
                      </div>
                    {:else}
                      <span class="text-slate">-</span>
                    {/if}
                  </td>
                  <td>
                    <div class="sym-cell">
                      <strong>{evt.symbol || '-'}</strong>
                      {#if evt.scrip_code}
                        <span class="scrip-code">({evt.scrip_code})</span>
                      {/if}
                    </div>
                  </td>
                  <td>
                    <span class="leg-tag">
                      {evt.leg_index !== undefined && evt.leg_index !== null ? `L${evt.leg_index}` : 'L0'}
                    </span>
                  </td>
                  <td>
                    {#if evt.side}
                      <span class="side-pill {evt.side === 'BUY' ? 'side-buy' : 'side-sell'}">
                        {evt.side}
                      </span>
                    {:else}
                      <span class="text-slate">-</span>
                    {/if}
                  </td>
                  <td class="numeric-cell">
                    <strong>{evt.fill_qty || evt.order_qty || '-'}</strong>
                  </td>
                  <td class="numeric-cell">
                    {#if evt.fill_price || evt.order_price}
                      <div class="price-cell">
                        <span>₹{Number(evt.fill_price || evt.order_price).toFixed(2)}</span>
                        {#if evt.slippage_paise}
                          <span class="slip-tag {evt.slippage_paise > 0 ? 'text-orange' : 'text-green'}">
                            {evt.slippage_paise}ps
                          </span>
                        {/if}
                      </div>
                    {:else}
                      <span class="text-slate">-</span>
                    {/if}
                  </td>
                  <td class="reason-cell">
                    <span class="reason-text" title={evt.trigger_reason || ''}>
                      {evt.trigger_reason || 'Autonomous Execution Event'}
                    </span>
                  </td>
                  <td class="actions-cell" on:click|stopPropagation>
                    <div class="action-btn-row">
                      <button class="icon-action-btn" title="Forensic Decision Card" on:click={() => openDecisionCard(evt)}>
                        🔍
                      </button>
                      {#if evt.signal_id}
                        <button class="icon-action-btn" title="Fan-out Allocation Inspector" on:click={() => openFanoutModal(evt.signal_id)}>
                          👥
                        </button>
                      {/if}
                      {#if evt.order_tag}
                        <button class="icon-action-btn" title="Order Lifecycle Timeline" on:click={() => openOrderTimeline(evt.order_tag)}>
                          ⏱️
                        </button>
                      {/if}
                    </div>
                  </td>
                </tr>
              {/each}
            {/if}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
</div>

<!-- Attached Inspector Modals -->
<AuditDecisionCardModal
  show={showDecisionModal}
  event={selectedDecisionEvent}
  onClose={() => (showDecisionModal = false)}
/>

<AuditFanoutModal
  show={showFanoutModal}
  signalId={selectedSignalId}
  environment={environment}
  onClose={() => (showFanoutModal = false)}
  onInspectOrder={(tag) => {
    showFanoutModal = false;
    openOrderTimeline(tag);
  }}
/>

<AuditOrderTimelineModal
  show={showTimelineModal}
  orderTag={selectedOrderTag}
  environment={environment}
  onClose={() => (showTimelineModal = false)}
  onInspectEvent={(ev) => {
    showTimelineModal = false;
    openDecisionCard(ev);
  }}
/>

<AuditReconModal
  show={showReconModal}
  environment={environment}
  onClose={() => (showReconModal = false)}
/>

<style>
  .audit-tab-wrapper {
    width: 100%;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  }
  .audit-controls-card {
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 8px;
    padding: 12px 16px;
  }
  .filter-row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px;
  }
  .filter-item {
    display: flex;
    flex-direction: column;
    min-width: 130px;
    flex: 1 1 130px;
  }
  .filter-item label {
    font-size: 10px;
    color: #94a3b8;
    text-transform: uppercase;
    font-weight: 600;
    margin-bottom: 4px;
  }
  .filter-item input, .filter-item select {
    background: #0f172a;
    border: 1px solid #334155;
    color: #f8fafc;
    border-radius: 6px;
    padding: 6px 8px;
    font-size: 12px;
    outline: none;
  }
  .filter-item input:focus, .filter-item select:focus {
    border-color: #38bdf8;
  }

  .time-toggle {
    display: flex;
    border: 1px solid #334155;
    border-radius: 6px;
    overflow: hidden;
  }
  .time-toggle button {
    background: #0f172a;
    border: none;
    color: #94a3b8;
    padding: 6px 10px;
    font-size: 11px;
    cursor: pointer;
    flex: 1;
  }
  .time-toggle button.active {
    background: #38bdf8;
    color: #0f172a;
    font-weight: bold;
  }

  .action-buttons-group {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
  }

  .audit-ledger-card {
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 8px;
    overflow: hidden;
  }
  .gap-8 { gap: 8px; }
  .env-pill {
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
  }
  .pill-sim { background: #ca8a04; color: #fef08a; }
  .pill-live { background: #047857; color: #a7f3d0; }

  .keyset-nav {
    display: flex;
    gap: 6px;
  }

  .table-scroll-wrap {
    overflow-x: auto;
  }
  .audit-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }
  .audit-table th {
    background: #0f172a;
    color: #94a3b8;
    font-size: 11px;
    text-transform: uppercase;
    font-weight: 600;
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid #334155;
  }
  .audit-table td {
    padding: 8px 12px;
    border-bottom: 1px solid #334155;
    color: #e2e8f0;
  }

  .audit-row {
    cursor: pointer;
    transition: background 0.15s ease;
  }
  .audit-row:hover {
    background: #273549;
  }

  .time-cell code {
    background: #0f172a;
    color: #38bdf8;
    padding: 2px 5px;
    border-radius: 4px;
    font-size: 11px;
  }

  .event-pill {
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 700;
  }
  .bg-green { background: #065f46; color: #34d399; }
  .bg-red { background: #7f1d1d; color: #f87171; }
  .bg-purple { background: #581c87; color: #c084fc; }
  .bg-blue { background: #1e3a8a; color: #60a5fa; }
  .bg-orange { background: #7c2d12; color: #fb923c; }
  .bg-slate { background: #334155; color: #94a3b8; }

  .user-cell {
    display: flex;
    flex-direction: column;
  }
  .ucc-name {
    color: #38bdf8;
  }
  .user-fullname {
    font-size: 10px;
    color: #94a3b8;
  }

  .sym-cell {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .scrip-code {
    font-size: 10px;
    color: #64748b;
  }
  .leg-tag {
    background: #334155;
    color: #94a3b8;
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 10px;
  }

  .side-pill {
    padding: 1px 5px;
    border-radius: 3px;
    font-size: 10px;
    font-weight: bold;
  }
  .side-buy { background: #065f46; color: #34d399; }
  .side-sell { background: #7f1d1d; color: #f87171; }

  .price-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }
  .slip-tag {
    font-size: 9px;
  }

  .reason-cell {
    max-width: 260px;
  }
  .reason-text {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #cbd5e1;
    font-size: 11px;
  }

  .actions-cell {
    width: 90px;
  }
  .action-btn-row {
    display: flex;
    gap: 4px;
  }
  .icon-action-btn {
    background: #0f172a;
    border: 1px solid #334155;
    border-radius: 4px;
    padding: 3px 6px;
    cursor: pointer;
    font-size: 12px;
    transition: background 0.15s;
  }
  .icon-action-btn:hover {
    background: #38bdf8;
  }

  .empty-row {
    text-align: center;
    padding: 36px !important;
    color: #64748b;
  }
  .spinner-inline {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid #334155;
    border-top-color: #38bdf8;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    vertical-align: middle;
    margin-right: 6px;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .numeric-cell {
    text-align: right;
  }
  .text-green { color: #34d399 !important; }
  .text-red { color: #f87171 !important; }
  .text-orange { color: #fb923c !important; }
  .text-slate { color: #94a3b8 !important; }
</style>
